import type { GeoPermissionState, GpsTrackingStatus } from '../types';
import { REFINED_TRACKING_OPTIONS, requestGeolocationPermission,
  startLocationWatch, stopLocationWatch, NormalizedLocationError } from './locationService.js';

export interface GpsState {
  permission: GeoPermissionState; status: GpsTrackingStatus;
  position: GeolocationPosition | null; error: string | null;
}
const browserApi = {
  permission: requestGeolocationPermission, watch: startLocationWatch, clearWatch: stopLocationWatch,
};

// Keep one watch for the visit. A timeout does not end a browser GPS watch.
// New requests, permission-change restarts, and polling can repeat permission prompts.
export class GpsController {
  state: GpsState;
  private enabled = false;
  private visible = true;
  private generation = 0;
  private watchId: number | null = null;
  private starting = false;
  constructor(private onChange: (state: GpsState) => void, private api = browserApi,
    initial: GeolocationPosition | null = null) {
    this.state = { permission: 'prompt', status: 'idle', position: initial, error: null };
  }
  private update(patch: Partial<GpsState>) { this.state = { ...this.state, ...patch }; this.onChange(this.state); }
  private clear() {
    this.generation++; this.starting = false;
    if (this.watchId !== null) this.api.clearWatch(this.watchId);
    this.watchId = null;
  }
  async start(force = false, allowPrompt = true) {
    this.enabled = true;
    if (force) this.clear();
    if (!this.visible || this.watchId !== null || this.starting) return;
    this.starting = true;
    const generation = this.generation;
    this.update({ status: 'starting', error: null });
    const permission = await this.api.permission();
    if (generation !== this.generation || !this.enabled) return;
    this.starting = false;
    this.update({ permission });
    if (permission === 'denied' || permission === 'unsupported') {
      this.update({ status: 'error', error: permission === 'denied'
        ? 'Location is blocked. Allow location for this site in your browser settings, and check your phone’s Location Services.'
        : 'Location requires a supported browser and HTTPS (or localhost).' });
      return;
    }
    if (permission === 'prompt' && !allowPrompt) {
      this.update({ status: 'error', error: 'Location access ended. Choose Enable location to allow it again.' });
      return;
    }
    if (!this.visible) { this.update({ status: 'paused' }); return; }
    const active = () => generation === this.generation && this.enabled && this.visible;
    const success = (position: GeolocationPosition) => {
      if (!active()) return;
      const { latitude, longitude } = position.coords;
      if (!Number.isFinite(latitude) || !Number.isFinite(longitude) || Math.abs(latitude) > 90 || Math.abs(longitude) > 180) return;
      if (!Number.isFinite(position.timestamp) || position.timestamp < (this.state.position?.timestamp || 0)) return;
      this.update({ permission: 'granted', status: 'tracking', position, error: null });
    };
    const failure = (error: NormalizedLocationError) => {
      if (!active()) return;
      if (error.code === 1 || error.code === 'UNSUPPORTED') {
        this.clear();
        this.update({ permission: error.code === 1 ? 'denied' : 'unsupported', status: 'error',
          error: error.code === 1 ? 'Location is blocked. Allow location for this site in your browser settings, and check your phone’s Location Services.' : error.message });
        return;
      }
      // Retain the active watch: it can deliver another position after a timeout.
      // Never create a periodic request while a permission prompt may be pending.
      this.update({ status: 'starting', error: this.state.position
        ? 'Showing your last known location while waiting for GPS.'
        : 'Still looking for your location. Check Location Services or move somewhere with a clearer signal.' });
    };
    const id = this.api.watch(success, failure, REFINED_TRACKING_OPTIONS);
    if (generation === this.generation) this.watchId = id;
    else if (id !== null) this.api.clearWatch(id);
  }
  setVisible(visible: boolean) {
    if (visible === this.visible) return;
    this.visible = visible;
    if (!this.enabled) return;
    // Let the browser suspend/resume the same watch when a phone sleeps.
    if (visible) {
      if (this.watchId !== null) this.update({ status: 'starting' });
      else this.recover();
    } else this.update({ status: 'paused' });
  }
  permissionChanged(permission: GeoPermissionState) {
    this.update({ permission });
    if (permission === 'granted') { this.recover(); return; }
    if (permission === 'prompt' || permission === 'denied') {
      this.clear();
      if (this.enabled) this.update({ status: 'error', error: permission === 'prompt'
        ? 'Location access ended. Choose Enable location to allow it again.'
        : 'Location is blocked. Allow location for this site in your browser settings, and check your phone’s Location Services.' });
    }
  }
  recover() { if (this.enabled && this.visible && this.watchId === null && !this.starting) void this.start(false, false); }
  stop() { this.enabled = false; this.clear(); this.update({ status: 'stopped', error: null }); }
  dispose() { this.enabled = false; this.clear(); }
}
